import { useEffect, useMemo, useState } from 'react';
import { Button, FormLabel, Group, Typography } from '@screentone/core';
import { useQueryClient } from '@tanstack/react-query';
import { JsonEditor } from 'json-edit-react';

import { ResponsiveLoader } from 'components';
import { useAlert } from 'contexts/alert/useAlert';
import { usePageLock } from 'contexts/page-lock/usePageLock';
import { PublicationSettingUpdateInput, usePublicationSettingUpdateMutation } from 'data/generated/graphql';
import { useConvertedProperty } from 'hooks';
import { usePublicationSettings } from 'hooks/publication-settings';
import { useJsonField } from 'hooks/useJsonField';

function SiteConfigLayoutsGeneral() {
  const currentProperty = useConvertedProperty();
  const queryClient = useQueryClient();
  const { alertError, alertSuccess } = useAlert();
  const { state: lockState } = usePageLock();
  const { isReadOnly } = lockState.publishing;

  const publicationSettingsQueryVariables = { publicationKey: currentProperty ?? '' };
  const { data } = usePublicationSettings(publicationSettingsQueryVariables);
  const { mutate, isLoading: isUpdating } = usePublicationSettingUpdateMutation();

  const collectionIds = useJsonField<Record<string, unknown>>(data?.publicationSetting.collectionIds);
  const [hasChanged, setHasChanged] = useState(false);

  useEffect(() => {
    setHasChanged(false);
  }, [data]);

  const jsonFields = useMemo(() => [collectionIds], [collectionIds]);

  const handleSave = () => {
    if (!data?.publicationSetting || !currentProperty) return;
    let parsedCollectionIds: Record<string, string> | undefined;
    try {
      parsedCollectionIds = collectionIds.parseValue()?.getJSON() as Record<string, string> | undefined;
    } catch (err) {
      alertError(`Cannot save — fix the highlighted JSON field(s) first. (${(err as Error).message})`);
      return;
    }

    const input: PublicationSettingUpdateInput = {
      ...(data.publicationSetting as unknown as PublicationSettingUpdateInput),
      collectionIds: parsedCollectionIds
    };

    mutate(
      { publicationSettingUpdateInput: input },
      {
        onSuccess: async () => {
          setHasChanged(false);
          jsonFields.forEach((field) => field.clearError());
          alertSuccess('General settings saved successfully');
          await queryClient.invalidateQueries({
            queryKey: ['PublicationSettings', publicationSettingsQueryVariables],
            exact: false
          });
        },
        onError: (err) => {
          alertError(`Failed to save: ${(err as Error).message}`);
        }
      }
    );
  };

  if (!data) {
    return <ResponsiveLoader />;
  }

  return (
    <Group gap="lg" direction="column" fullWidth>
      <Group.Item fullWidth>
        <Group align="end">
          <Button primary onClick={handleSave} disabled={!hasChanged || isUpdating || isReadOnly}>
            {isUpdating ? 'Saving…' : 'Save'}
          </Button>
        </Group>
      </Group.Item>
      <Group.Item fullWidth>
        <Group gap="xs" direction="column" fullWidth>
          <FormLabel label="Collection IDs" labelPosition="top" fullWidth />
          <Typography variant="note">
            Per-pub collection IDs (Cxense, video playlists, env-prefixed UCS collections) keyed by stable name.
          </Typography>
          <JsonEditor
            data={collectionIds.parseValue()?.getJSON() ?? {}}
            indent={2}
            setData={(value) => {
              collectionIds.handleChange(JSON.stringify(value));
              setHasChanged(true);
            }}
            restrictTypeSelection={['string']}
            maxWidth="100%"
            collapse
            restrictEdit={isReadOnly}
            restrictDelete={isReadOnly}
            restrictAdd={isReadOnly}
          />
        </Group>
      </Group.Item>
    </Group>
  );
}

export default SiteConfigLayoutsGeneral;
